import type { FormEvent, ReactNode } from "react";
import { useId, useState } from "react";
import { cn, tv } from "tailwind-variants";
import { m } from "#/paraglide/messages";

export type SearchFieldSize = "small" | "default" | "large";
export type SearchFieldIntent =
	| "primary"
	| "secondary"
	| "light"
	| "dark"
	| "white"
	| "black"
	| "success"
	| "warning"
	| "danger"
	| "info"
	| "none";
export type SearchFieldVariant =
	| "solid"
	| "outline"
	| "ghost"
	| "soft"
	| "link";

export type SearchFieldProps = {
	textLabel?: string;
	body?: ReactNode;
	size?: SearchFieldSize;
	intentColor?: SearchFieldIntent;
	typeVariant?: SearchFieldVariant;
	placeholder?: string;
	searchLabel?: string;
	searchPageUrl?: string;
	customSearchEngine?: boolean;
	customUrl?: string;
	customTargetBlank?: boolean;
	onSearch?: (query: string) => void;
	className?: string;
};

const searchField = tv({
	slots: {
		inner: "search-inner",
		label: "form-label",
		input: "form-control form-control-search",
		button: "btn btn-icon-only btn-search",
		body: "form-text mb-0-last",
	},
	variants: {
		size: {
			small: {
				inner: "search-inner-sm",
				input: "form-control-sm",
				button: "btn-sm",
			},
			default: {},
			large: {
				inner: "search-inner-lg",
				input: "form-control-lg",
				button: "btn-lg",
			},
		},
		intentColor: {
			primary: { button: "btn-primary" },
			secondary: { button: "btn-secondary" },
			light: { button: "btn-light" },
			dark: { button: "btn-dark" },
			white: { button: "btn-white" },
			black: { button: "btn-black" },
			success: { button: "btn-success" },
			warning: { button: "btn-warning" },
			danger: { button: "btn-danger" },
			info: { button: "btn-info" },
			none: { button: "btn-none" },
		},
		typeVariant: {
			solid: {},
			outline: { button: "btn-outline" },
			ghost: { button: "btn-ghost" },
			soft: { button: "btn-soft" },
			link: { button: "btn-link" },
		},
	},
});

function SearchIcon() {
	return (
		<svg className="icon icon-24" aria-hidden="true">
			<use href="/umbrella/ui/icons/lucide-search.svg#icon" />
		</svg>
	);
}

function resolveSearchUrl({
	query,
	customSearchEngine,
	customUrl,
	searchPageUrl,
}: {
	query: string;
	customSearchEngine?: boolean;
	customUrl?: string;
	searchPageUrl?: string;
}) {
	if (customSearchEngine && customUrl?.includes("%s")) {
		return customUrl.replace("%s", encodeURIComponent(query));
	}

	if (!searchPageUrl) {
		return null;
	}

	const separator = searchPageUrl.includes("?") ? "&" : "?";
	return `${searchPageUrl}${separator}q=${encodeURIComponent(query)}`;
}

export function SearchField({
	textLabel,
	body,
	size = "default",
	intentColor = "primary",
	typeVariant = "solid",
	placeholder,
	searchLabel,
	searchPageUrl = "/search",
	customSearchEngine = false,
	customUrl,
	customTargetBlank = false,
	onSearch,
	className,
}: SearchFieldProps) {
	const inputId = useId();
	const [query, setQuery] = useState("");
	const resolvedPlaceholder = placeholder ?? m.search();
	const resolvedSearchLabel = searchLabel ?? m.search();
	const {
		inner,
		label,
		input,
		button,
		body: bodyClass,
	} = searchField({
		size,
		intentColor,
		typeVariant,
	});
	const useCustomRedirect =
		customSearchEngine && Boolean(customUrl?.includes("%s"));
	const formAction = useCustomRedirect ? "" : searchPageUrl;

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const trimmed = query.trim();
		if (!trimmed) {
			return;
		}

		onSearch?.(trimmed);

		const targetUrl = resolveSearchUrl({
			query: trimmed,
			customSearchEngine,
			customUrl,
			searchPageUrl,
		});

		if (!targetUrl) {
			return;
		}

		if (useCustomRedirect && customTargetBlank) {
			window.open(targetUrl, "_blank", "noopener,noreferrer");
			return;
		}

		window.location.assign(targetUrl);
	};

	const searchFieldContent = (
		<>
			{textLabel ? (
				<label htmlFor={inputId} className={label()}>
					{textLabel}
				</label>
			) : null}
			<div className={inner()}>
				<input
					type="search"
					id={inputId}
					name="q"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					className={input()}
					placeholder={resolvedPlaceholder}
					aria-label={resolvedPlaceholder}
				/>
				<button type="submit" className={button()}>
					<SearchIcon />
					<span className="sr-only">{resolvedSearchLabel}</span>
				</button>
			</div>
		</>
	);

	return (
		<div className={cn("block__search-field search-field-wrapper", className)}>
			{useCustomRedirect ? (
				<form
					action={formAction}
					method="get"
					data-custom-url={customUrl}
					data-target={customTargetBlank ? "blank" : "self"}
					onSubmit={handleSubmit}
				>
					<div className="search-outer">{searchFieldContent}</div>
				</form>
			) : (
				<form
					action={formAction}
					method="get"
					className="search-outer"
					onSubmit={handleSubmit}
				>
					{searchFieldContent}
				</form>
			)}
			{body ? <div className={bodyClass()}>{body}</div> : null}
		</div>
	);
}
